<template>
  <div class="list">
    <navbar :docsName="docsName" />
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="container-fluid">
            <head-menu
              :col="12"
              class="d-xl-flex"
              :headline="meinAblauf ? meinAblauf.bezeichnung : ''"
              :gesamtanzahl="gesamtanzahl"
            ></head-menu>

            <div v-if="fehlermeldung" class="row mb-2">
              <div class="block br-t-l-0 w-100-p br-t-l-xl-0 p-3">
                <div class="alert alert-danger mb-0">{{ fehlermeldung }}</div>
              </div>
            </div>

            <template v-else-if="meinAblauf">
              <!-- Der Knopf des Ablaufs mit einem Satz, was er tut (9.3) -->
              <div class="row mb-2">
                <div class="block br-t-l-0 w-100-p br-t-l-xl-0 p-3 d-flex align-items-center flex-wrap">
                  <button v-if="knopfErlaubt" type="button" class="btn btn-primary mr-3" @click="anfordern">
                    {{ knopfText }}
                  </button>
                  <span class="satz">{{ knopfSatz }}</span>
                </div>
              </div>

              <!-- Stand: nur Text, keine Schalter (E4) -->
              <div class="row mb-2">
                <div class="block w-100-p p-3">
                  <div class="panel">
                    <div>
                      <strong>{{ $t("global.automaticrun") }}:</strong>
                      {{ meinAblauf.automatisch ? $t("global.switchedon") : $t("global.switchedoff") }}
                      <template v-if="meinAblauf.automatisch && meinAblauf.naechsterLauf">
                        · {{ $t("global.nextrun") }} {{ meinAblauf.naechsterLauf }}
                        <template v-if="meinAblauf.uebernaechsterLauf">, {{ $t("global.then") }} {{ meinAblauf.uebernaechsterLauf }}</template>
                      </template>
                    </div>
                    <div v-if="letzterLauf">
                      <!-- mr-1: Vue entfernt den Zeilenumbruch zwischen zwei Elementen samt Leerzeichen -->
                      <strong class="mr-1">{{ $t("global.lastrun") }}:</strong>
                      <span :class="ergebnisKlasse(letzterLauf)">{{ ergebnisText(letzterLauf, uebersetze) }}</span>
                      · {{ zeitraumText(letzterLauf) }}
                      <template v-if="zusammenfassungText(letzterLauf)">
                        · <span :class="{ 'text-muted': zusammenfassungIstMeldung(letzterLauf) }">{{ zusammenfassungText(letzterLauf) }}</span>
                      </template>
                    </div>
                    <div v-else><strong>{{ $t("global.lastrun") }}:</strong> {{ $t("global.noneyet") }}</div>
                  </div>
                  <slot name="stand"></slot>
                </div>
              </div>

              <slot name="aufmerksamkeit"></slot>

              <!-- Reiter Läufe mit Filterzeile (9.3) -->
              <ul class="nav nav-tabs reiter mt-2">
                <li class="nav-item">
                  <a class="nav-link active" href="#" @click.prevent>{{ $t("global.runs") }}</a>
                </li>
              </ul>
              <div class="row mb-2">
                <div class="block br-t-l-0 w-100-p p-3">
                  <div class="row">
                    <div class="col-xl-3">
                      <div class="form-group">
                        <select v-model="filter.laufart" class="form-control">
                          <option value="">{{ $t("global.all") }}</option>
                          <option v-for="l in meinAblauf.laufarten" :key="l.kennung" :value="l.kennung">{{ l.bezeichnung }}</option>
                        </select>
                        <label>{{ $t("global.runtype") }}</label>
                      </div>
                    </div>
                    <div class="col-xl-2">
                      <div class="form-group">
                        <select v-model="filter.ausloeser" class="form-control">
                          <option v-for="a in ausloeser" :key="a.key" :value="a.key">{{ uebersetzt(uebersetze, a) }}</option>
                        </select>
                        <label>{{ $t("global.trigger") }}</label>
                      </div>
                    </div>
                    <div class="col-xl-2">
                      <div class="form-group">
                        <select v-model="filter.ergebnis" class="form-control">
                          <option v-for="e in ergebnisse" :key="e.key" :value="e.key">{{ uebersetzt(uebersetze, e) }}</option>
                        </select>
                        <label>{{ $t("global.result") }}</label>
                      </div>
                    </div>
                    <div class="col-xl-2">
                      <div class="form-group">
                        <input v-model.lazy.trim="filter.id" class="form-control input" type="text" placeholder=" " />
                        <label>{{ $t("global.runid") }}</label>
                      </div>
                    </div>
                  </div>
                  <lauf-tabelle
                    ref="tabelle"
                    :filter="parameter"
                    :felder="felder"
                    @geladen="gesamtanzahl = $event.gesamtanzahl"
                  />
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <lauf-ansicht />
  </div>
</template>

<script>
import AppApi from "@/api/AppApi";
import HeadMenu from "@/components/HeadMenu";
import Navbar from "@/components/Navbar";
import LaufAnsicht from "@/components/Lauf/LaufAnsicht.vue";
import LaufTabelle from "@/components/Lauf/LaufTabelle.vue";
import { apiErrorToAlert } from "@/utils/Errorhandler";
import {
  AUSLOESER_FILTER,
  ERGEBNIS_FILTER,
  ergebnisKlasse,
  ergebnisText,
  zusammenfassungIstMeldung,
  uebersetzt,
  zusammenfassungText,
} from "@/components/Lauf/LaufTexte";

/**
 * Rahmen für die Seite je Ablauf (Konzept Steuerungen und Schnittstellen 9.3, Board „Schnittstelle Topal“). Kennt
 * keinen bestimmten Ablauf: Bezeichnung, Stand, Laufarten und Rechte kommen aus GET lauf/ablaeufe/.
 *
 * - ablauf: Kennung des Ablaufs, etwa lauf.test
 * - knopfLaufart, knopfText, knopfSatz: der Knopf des Ablaufs, sichtbar nur, wenn der Benutzer die Laufart
 *   anfordern darf, und der Satz, was er tut
 * - knopfAnforderung: weitere Angaben für lauf-anfordern (parameter, test, objektArt, objektId), etwa das
 *   Formular des Testablaufs
 * - Slot stand: unter dem Stand, was der Ablauf ergänzt (bei Topal „Stand Topal“ und die Verbindung)
 * - Slot aufmerksamkeit: was Aufmerksamkeit braucht (bei Topal die zurückgestellten und abgelehnten Aufträge)
 */
export default {
  name: "AblaufSeite",
  components: { Navbar, HeadMenu, LaufAnsicht, LaufTabelle },
  props: {
    ablauf: { type: String, required: true },
    knopfLaufart: { type: String, required: true },
    knopfText: { type: String, required: true },
    knopfSatz: { type: String, default: "" },
    knopfAnforderung: { type: Object, default: () => ({}) },
    docsName: { type: String, default: "" },
  },
  data() {
    return {
      ablaeufe: [],
      fehlermeldung: null,
      gesamtanzahl: null,
      ausloeser: AUSLOESER_FILTER,
      ergebnisse: ERGEBNIS_FILTER,
      filter: { laufart: "", ausloeser: "alle", ergebnis: "alle", id: "" },
      felder: ["zeitpunkt", "laufart", "ausloeser", "dauer", "ergebnis", "zusammenfassung", "id"],
    };
  },
  computed: {
    /** Dieser Ablauf aus der Liste aller angemeldeten Abläufe */
    meinAblauf() {
      return this.ablaeufe.find((a) => a.kennung === this.ablauf);
    },
    letzterLauf() {
      return this.meinAblauf ? this.meinAblauf.letzterLauf : null;
    },
    /** Darf der Benutzer die Laufart des Knopfs anfordern? Steht je Laufart in GET lauf/ablaeufe/ */
    knopfErlaubt() {
      if (!this.meinAblauf) return false;
      const laufart = this.meinAblauf.laufarten.find((a) => a.kennung === this.knopfLaufart);

      return !!laufart && laufart.darfAnfordern;
    },
    /** Die Filterzeile als Parameter für GET lauf/, immer mit dem Ablauf */
    parameter() {
      const f = this.filter;
      return {
        ablauf: this.ablauf,
        laufart: f.laufart,
        id: f.id,
        status: ERGEBNIS_FILTER.find((e) => e.key === f.ergebnis).status,
        ...AUSLOESER_FILTER.find((a) => a.key === f.ausloeser).parameter,
      };
    },
  },
  created() {
    this.ablaeufeLaden();
  },
  mounted() {
    this.$bus.$on("lauf-ansicht-geschlossen", this.neuLaden);
  },
  beforeDestroy() {
    this.$bus.$off("lauf-ansicht-geschlossen", this.neuLaden);
  },
  methods: {
    ergebnisKlasse,
    ergebnisText,
    zusammenfassungIstMeldung,
    zusammenfassungText,
    uebersetzt,
    uebersetze(schluessel) {
      return this.$t(schluessel);
    },
    ablaeufeLaden() {
      AppApi.get("lauf/ablaeufe/", { redirectOnError: false })
        .then((response) => {
          this.ablaeufe = response.data;
          if (!this.meinAblauf) this.fehlermeldung = this.$t("global.workflownotregistered", { workflow: this.ablauf });
        })
        .catch((e) => {
          if (e.response && e.response.status === 403) {
            this.fehlermeldung = e.response.data;
          } else {
            this.$notify(apiErrorToAlert(e));
          }
        });
    },
    /** Nach einem Lauf: Stand und Läufe neu */
    neuLaden() {
      this.ablaeufeLaden();
      if (this.$refs.tabelle) this.$refs.tabelle.vonVorn();
    },
    anfordern() {
      this.$bus.$emit("lauf-anfordern", {
        ...this.knopfAnforderung,
        ablauf: this.ablauf,
        laufart: this.knopfLaufart,
      });
    },
    /** „29.10.2026 11:28 bis 11:30“, bei einem anderen Tag am Ende mit Datum */
    zeitraumText(lauf) {
      const von = lauf.gestartetAm || lauf.angefordertAm;
      if (!lauf.beendetAm) return `${this.$t("global.since")} ${von}`;
      const [vonDatum] = von.split(" ");
      const [bisDatum, bisZeit] = lauf.beendetAm.split(" ");
      return this.$t("global.fromtotime", { from: von, to: vonDatum === bisDatum ? bisZeit : lauf.beendetAm });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/scss/colors";

.panel {
  background: $very-light-blue;
  border-radius: 10px;
  padding: 0.6rem 1rem;
}
.satz {
  font-size: 14px;
}
.text-bernstein {
  color: #a54800;
  font-weight: 600;
}
.text-rot {
  color: #ae2e24;
  font-weight: 600;
}
</style>
